import React, { useEffect, useCallback } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';
import {
  FormCard,
  YupNumeric,
  YupNumericReq,
  YupStringReq,
  promiseModalDialog,
  toastSuccess,
  toastWarning,
  toastFailure,
} from 'motion-components';
import * as Yup from 'yup';
import * as Lo from 'lodash';
import {
  gqlGetPostalcode2ById,
  gqlUpdatePostalcode2,
  gqlGetRegions,
  gqlCreatePostalcode2,
  gqlDeletePostalcode2,
  gqlGetPostalcode2,
} from '../graphql';
import { FormFields } from './form-fields';
// import { useHandleError } from 'shared-pages/services';
import { useTranslation } from 'react-i18next';
import { getRouteByName } from 'app/routes';
import { useHandleError } from 'shared-pages/services';
import { useApolloClient } from '@apollo/client';

export const Postalcode2FormPage = () => {
  const { handleGraphqlError } = useHandleError('Postalcode2FormPage');
  const history = useHistory();
  const location: any = useLocation();
  const { id: rawId } = useParams<{ id: string }>();
  const n = Number(rawId);
  const isNew = rawId === 'new' || !Number.isFinite(n) || n <= 0; // 'new' | NaN | 0 | -1 → create
  const idNum = isNew ? 0 : n;
  const client = useApolloClient();

  const { t } = useTranslation();

  const formShape = {
    id: YupNumeric,
    postalcode: YupStringReq,
    regionId: YupNumericReq,
  };

  const validationSchema = Yup.object().shape(formShape);
  const pickProperties = (values) => Lo.pick(values, Lo.keys(formShape));
  // type FormValuesType = Yup.InferType<typeof validationSchema>;
  type FormValuesType = {
    id: number | null;
    postalcode: string;
    regionId: number | null;
  };

  // const [checkExists] = useLazyQuery(gqlGetPostalcode2, {
  //   fetchPolicy: 'network-only',
  // });

  const [updateRecord] = useMutation(gqlUpdatePostalcode2, {
    onError: (e) => toastWarning(e.message),
  });

  const [createRecord] = useMutation(gqlCreatePostalcode2, {
    onError: (e) => toastWarning(e.message),
  });

  const [deleteRecord] = useMutation(gqlDeletePostalcode2, {
    onError: (e) => toastWarning(e.message),
  });

  const [get, { data: rawData, error, refetch }] = useLazyQuery(gqlGetPostalcode2ById, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const data = rawData ? rawData.getPostalcode2ById : undefined;
  const base = data ? (pickProperties(data) as any) : { id: null, postalcode: '', regionId: null };

  const initialValues = {
    ...base,
    area: data?.area ?? '',
    city: data?.city ?? '',
    county: data?.county ?? '',
    country: data?.country ?? '',
    isActive: data?.isActive ?? true,
  };

  const {
    data: getRegionsRes,
    loading: regionsLoading,
    error: getRegionsError,
  } = useQuery(gqlGetRegions, { variables: { limit: 500, order: 'name asc' } });

  const regions: { id: number; name: string }[] = getRegionsRes?.getRegions?.data ?? [];

  const handleDelete = useCallback(async () => {
    const res = await promiseModalDialog({
      title: t('Διαγραφή Ταχυδρομικού Κώδικα'),
      text: t('Είστε σίγουροι ότι θέλετε να διαγράψετε τον Ταχυδρομικό Κωδικό;'),
    });
    if (res) {
      deleteRecord({ variables: { id: idNum } })
        .then(() => {
          toastSuccess(`Επιτυχής Διαγραφή Ταχυδρομικού Κώδικα [${idNum}]`);
          history.goBack();
        })
        .catch((error) => {
          handleGraphqlError('mutation-delete', error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteRecord, idNum]);

  useEffect(() => {
    if (!isNew) get({ variables: { id: idNum } });
  }, [get, idNum, isNew]);

  const handleReturn = useCallback(() => {
    history.push(generatePath(getRouteByName('Postalcodes2')), { ...location.state });
  }, [history, location.state]);

  const handleSubmit = async (values: any, actions: any) => {
    try {
      // normalize once
      const pc = String(values.postalcode ?? '')
        .trim()
        .replace(/\s+/g, '');
      const regionIdNum = Number(values.regionId);

      const common = {
        postalcode: pc,
        regionId: regionIdNum,
        area: values.area ?? null,
        city: values.city ?? null,
        county: values.county ?? null,
        country: values.country ?? null,
        isActive: typeof values.isActive === 'boolean' ? values.isActive : true,
      };

      if (isNew) {
        // normalized input
        const pc = String(values.postalcode ?? '')
          .trim()
          .replace(/\s+/g, '');
        const regionIdNum = Number(values.regionId);

        const whereObj = { postalcode: { $eq: pc } };

        const { data: existsRes } = await client.query({
          query: gqlGetPostalcode2,
          variables: { limit: 50, where: JSON.stringify(whereObj) },
          fetchPolicy: 'network-only',
        });

        const list = existsRes?.getPostalcode2?.data ?? [];

        // 2) client-side strict check against normalized values
        const dup = list.some((r: any) => String(r.postalcode).trim().replace(/\s+/g, '') === pc);

        if (dup) {
          toastFailure('Ο Τ.Κ που προσπαθήσατε να προσθέσετε, υπάρχει ήδη!');
          actions.setSubmitting(false);
          return;
        }

        const { data: m } = await createRecord({ variables: { data: common } });
        const createdId = m?.createPostalcode2?.id;
        if (!createdId) throw new Error('Create failed');

        toastSuccess(`Επιτυχής Δημιουργία [${createdId}]`);
        actions.setSubmitting(false);

        history.replace(generatePath(getRouteByName('Postalcodes2Form'), { id: createdId }), {
          ...location.state,
        });
        return;
      }

      // UPDATE path
      const payload = { id: idNum, ...common };
      const changed =
        payload.postalcode !== (data?.postalcode ?? '') ||
        payload.regionId !== (data?.regionId ?? null) ||
        payload.area !== (data?.area ?? null) ||
        payload.city !== (data?.city ?? null) ||
        payload.county !== (data?.county ?? null) ||
        payload.country !== (data?.country ?? null) ||
        (typeof values.isActive === 'boolean' ? values.isActive : data?.isActive ?? true) !==
          (data?.isActive ?? true);

      if (!changed) throw new Error('Δεν υπάρχουν αλλαγές');

      const { data: m } = await updateRecord({
        variables: { data: payload },
        refetchQueries: [{ query: gqlGetPostalcode2ById, variables: { id: idNum } }],
        awaitRefetchQueries: true,
      });

      if (!m?.updatePostalcode2?.id) throw new Error('Update failed');
      toastSuccess(`${t('Επιτυχής Ενημέρωση Εγγραφής')} [${m.updatePostalcode2.id}]`);
    } catch (err: any) {
      toastWarning(err?.message ?? 'Mutation failed');
      actions.setSubmitting(false);
    } finally {
      if (!isNew) actions.setSubmitting(false);
    }
  };

  if (error || getRegionsError) {
    // handleGraphqlError('query-get', error);
    return <h3 className="text-center">{error?.message ?? getRegionsError?.message}</h3>;
  }

  return (
    <FormCard
      title={`${t('Τ.Κ.')}: ${data?.postalcode || initialValues.postalcode}`}
      onSubmit={handleSubmit}
      onDelete={isNew ? undefined : handleDelete}
      onReturn={handleReturn}
      permitSubmit={false}
      permitDelete={true}
      noButtons={false}
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      idx={idNum}
    >
      <FormFields
        updateRecord={updateRecord}
        createRecord={createRecord}
        deleteRecord={deleteRecord}
        refetch={refetch}
        data={data}
        regions={regions}
        regionsLoading={regionsLoading}
        isNew={isNew}
      />
    </FormCard>
  );
};
